import { CurrencyInput, DateTimePicker, Label, Textarea } from "@tellescope/ui";
import { Button, Select } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import { useTellescopeInfiniteQuery, useTellescopeMutation } from "@tellescope/tanstack-query";
import { toMajorCurrencyUnit, toSmallestCurrencyUnit } from "@tellescope/utils";
import { useRef } from "react";

export interface LogPurchaseFormProps {
  contactId: string;
  onSubmit?: () => void;
  onCancel?: () => void;
}

export const LogPurchaseForm = ({ contactId, onSubmit, onCancel }: LogPurchaseFormProps) => {
  const { t } = useTranslation("payments");

  const productsQuery = useTellescopeInfiniteQuery({
    model: "products",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flat(),
  });

  const logPurchaseMutation = useTellescopeMutation({
    model: "purchases",
    method: "createOne",
    onSuccess: () => {
      onSubmit?.();
    },
  });

  const initialPurchasedAtRef = useRef<Date | null>(null);
  if (initialPurchasedAtRef.current === null) {
    initialPurchasedAtRef.current = new Date();
  }

  const soleProduct = productsQuery.data?.length === 1 ? productsQuery.data[0] : undefined;

  const form = useForm({
    fields: {
      productId: {
        type: "select",
        initialValue: soleProduct?.id ?? "",
        required: true,
      },
      cost: {
        type: "number",
        initialValue: soleProduct ? toMajorCurrencyUnit(soleProduct.cost.amount) : 0,
        required: true,
        dependencies: ["productId"],
      },
      purchasedAt: {
        type: "date",
        initialValue: initialPurchasedAtRef.current,
      },
      notes: {
        type: "textarea",
        initialValue: "",
      },
    },
    validators: {
      cost: (value) => (value > 0 ? undefined : t("Cost must be greater than zero")),
    },
    validateOn: "blur",
    onSubmit: (values) => {
      logPurchaseMutation.mutate({
        title: t("Logged Purchase"),
        processor: "Stripe",
        enduserId: contactId,
        productId: values.productId,
        cost: {
          amount: toSmallestCurrencyUnit(values.cost),
          currency: "USD", // TODO: support other currencies
        },
        processedAt: values.purchasedAt,
        notes: values.notes,
      });
    },
    sideEffects: {
      productId: (value) => {
        const productCost = productsQuery.data?.find((product) => product.id === value)?.cost.amount;
        if (productCost === undefined) {
          return {};
        }

        return {
          cost: toMajorCurrencyUnit(productCost),
        };
      },
    },
  });

  return (
    <form className="flex flex-col gap-6" {...form.getFormProps()}>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 p-1">
            <Label htmlFor="product">{t("Select Product")}</Label>
            <Select {...form.getCustomFieldProps("productId")}>
              <Select.Trigger id="product" className="w-full">
                <Select.Value placeholder={t("select Product")} />
              </Select.Trigger>
              <Select.Content>
                {productsQuery.data?.map(({ id, title }) => (
                  <Select.Item key={id} value={id}>
                    {title}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </div>
          <div className="space-y-2 p-1">
            <Label htmlFor="cost" className={!form.values.productId ? "text-muted-foreground" : undefined}>
              {/* TODO: support other currencies */}
              {t("Cost in Dollars ($)")}
            </Label>
            <CurrencyInput
              {...form.getFieldProps("cost")}
              onChange={(value) => form.setFieldValue("cost", value ?? 0)}
              disabled={!form.values.productId}
            />
          </div>
          <DateTimePicker
            {...form.getCustomFieldProps("purchasedAt")}
            id="processedAt"
            className="space-y-2 p-1"
            label={t("Date")}
          />
          <div className="space-y-2 p-1">
            <Label htmlFor="notes">{t("Add Notes")}</Label>
            <Textarea id="notes" {...form.getFieldProps("notes")} />
          </div>
        </div>
      </div>
      <div className="w-full flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        {/* `form.isSubmitting` cannot gate this: `onSubmit` calls `.mutate`, which returns
            immediately, so the flag clears a microtask later while the write is still in flight —
            long enough for a second click to log the purchase twice. */}
        <Button disabled={!form.isValid || logPurchaseMutation.isPending} type="submit">
          {t("Save")}
        </Button>
      </div>
    </form>
  );
};
